import { useState, useMemo, useRef, useEffect, useLayoutEffect, Fragment } from "react";
import { createPortal } from "react-dom";
import {
  Search, X, CalendarDays, MapPin, ChevronDown, Check as CheckIcon,
  UserCircle2, UserX, RotateCw, Flame, Target, UserCog, Filter,
  UserCircle, Pencil, MailOpen, Sun, CalendarRange, CalendarOff, UserSearch, Repeat, FileText,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import SearchClearButton from "@/components/ui/SearchClearButton";
import SearchDebounceBar from "@/components/ui/SearchDebounceBar";
import SearchShortcutHint from "@/components/ui/SearchShortcutHint";
import { J3Groups, PANEL } from "@/components/common/ProjectJ3";

/*
 * ---------------------------------------------------------------------------
 * PortalDropdown — spec 2026-02-18 duodecies (fix scroll bug persistant)
 * ---------------------------------------------------------------------------
 * Le bug : les popovers positionnés en `absolute` à l'intérieur de la barre
 * de filtres se retrouvaient piégés dans le contexte flex/overflow du parent
 * (Tasks.jsx utilise height: calc(100vh - 80px) + chaîne de min-h-0), ce qui
 * faisait apparaître une scrollbar verticale à chaque ouverture — notamment
 * sur le chip "Siège / Projet" dont la liste est plus longue que la fenêtre.
 *
 * La solution robuste : rendre le popover DANS document.body via createPortal,
 * en `position: fixed`, positionné manuellement à partir du bounding rect du
 * trigger. Aucun ancêtre ne peut plus le clipper ni provoquer de scroll.
 * Repositionnement automatique au scroll/resize.
 */
function PortalDropdown({ anchorRef, open, onClose, minWidth = 220, maxHeight = 320, children, testId }) {
  const [pos, setPos] = useState(null);
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const update = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      // Default: open below the trigger
      let top = r.bottom + 4;
      let left = r.left;
      // If the popover would overflow the viewport bottom, open above
      const estimatedH = Math.min(maxHeight, 320);
      if (top + estimatedH > vh - 8 && r.top - estimatedH - 4 > 8) {
        top = r.top - estimatedH - 4;
      }
      // Clamp horizontally
      const estimatedW = Math.max(minWidth, r.width);
      if (left + estimatedW > vw - 8) left = Math.max(8, vw - estimatedW - 8);
      if (left < 8) left = 8;
      setPos({ top, left, width: estimatedW });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef, minWidth, maxHeight]);

  // Click outside → close (using capture so it beats stopPropagation
  // handlers inside the trigger).
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (contentRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [open, onClose, anchorRef]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={contentRef}
      className="rounded-md border shadow-xl"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        minWidth: pos.width,
        maxHeight,
        overflowY: "auto",
        background: "#1E1E1E",
        borderColor: "#3A3A3A",
        zIndex: 9999,
      }}
      data-testid={testId}
    >
      {children}
    </div>,
    document.body,
  );
}

// Spec iter122 (Feb 2026) — 7 statuts métier + Planifiée (état séparé,
// tâche à exécution différée). `completed` retiré : depuis le renommage
// « Terminée → Clôturée », `closed` est l'unique statut de fin. `À
// refaire` conservé (encore présent dans le backend/legacy tasks).
const STATUS_OPTS = [
  { v: "planned",     l: "Planifiée",   color: "#38BDF8" },
  { v: "waiting",     l: "En attente",  color: "#8a8a8f" },
  { v: "accepted",    l: "Acceptée",    color: "#5aa8e0" },
  { v: "in_progress", l: "En cours",    color: "#F97316" },
  { v: "late",        l: "En retard",   color: "#e07a7a" },
  { v: "relance",     l: "Relancé",     color: "#E0569B" },
  { v: "to_verify",   l: "À vérifier",  color: "#A855F7" },
  { v: "redo",        l: "À refaire",   color: "#F97316" },
  { v: "refused",     l: "Refusée",     color: "#EF4444" },
  { v: "closed",      l: "Clôturée",    color: "#22C55E" },
];

// Spec iter131 (Feb 2026) — 3 niveaux visibles ("Faible" retiré).
const PRIO_OPTS = [
  { v: "moyen",    l: "Normal",   color: "#22C55E" },
  { v: "urgent",   l: "Élevée",   color: "#F97316" },
  { v: "immediat", l: "Immédiat", color: "#EF4444" },
];

/*
 * QUICK_GROUPS — 3 groupes homogènes (spec 2026-02-18 point 4).
 *   1. "Les miennes"   — Assignées à moi · Créées par moi · Ma lecture
 *   2. "Temporel"      — Aujourd'hui · Cette semaine · Sans deadline
 *   3. "Organisation"  — Non assignées · Récurrentes · Priorité élevée
 * Séparateur vertical 1px #2a2a28 entre chaque groupe.
 * Chips fond #232320, radius 15px, icône colorée + texte, padding 7x13px.
 */
const QUICK_GROUPS = [
  { key: "les_miennes", items: [
    { v: "mine",         l: "Assignées à moi", Icon: UserCircle,  color: "#378ADD" },
    { v: "mine_created", l: "Créées par moi",  Icon: Pencil,      color: "#F9BC26" },
    { v: "mine_unread",  l: "Ma lecture",      Icon: MailOpen,    color: "#EF9F27" },
  ]},
  { key: "temporel", items: [
    { v: "today",        l: "Aujourd'hui",    Icon: Sun,           color: "#FAC775" },
    { v: "this_week",    l: "Cette semaine",  Icon: CalendarRange, color: "#85B7EB" },
    { v: "no_date",      l: "Sans deadline",  Icon: CalendarOff,   color: "#888780" },
  ]},
  { key: "organisation", items: [
    { v: "unassigned",     l: "Non assignées",   Icon: UserSearch, color: "#F0997B" },
    { v: "recurring",      l: "Récurrentes",     Icon: Repeat,     color: "#5DCAA5" },
    { v: "high_priority",  l: "Priorité élevée", Icon: Flame,      color: "#E24B4A" },
    // Spec 2026-02-XX rev16 (point J.9) — filtre rapide « Liées à un
    // document » pour isoler les tâches issues du suivi documentaire.
    { v: "linked_to_document", l: "Liées à un document", Icon: FileText, color: "#5A9EE0" },
  ]},
];

const NONE = "__all__";

/** Auto-detect if the search input looks like a reference (starts with `#`
 *  or matches the `TH00xx` pattern) and route the value to the dedicated
 *  `reference` filter instead of the free-text search. */
const REF_PATTERN = /^(?:#\s*|TH\s*)([A-Z0-9]{2,})$/i;

// ---------------- Period chip (date range with presets) -----------------
function fmtIsoDate(d) { return d.toISOString().slice(0, 10); }
function todayIso() { return fmtIsoDate(new Date()); }
function weekStartIso() {
  const d = new Date();
  const wd = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - wd);
  return fmtIsoDate(d);
}
function monthStartIso() {
  const d = new Date();
  d.setDate(1);
  return fmtIsoDate(d);
}

function PeriodChip({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(from || "");
  const [t, setT] = useState(to || "");
  const anchorRef = useRef(null);
  const on = !!(from || to);
  let label = "Période…";
  if (from && to && from === to) {
    if (from === todayIso()) label = "Aujourd'hui";
    else label = `Le ${from}`;
  } else if (from || to) {
    label = `Du ${from || "…"} au ${to || "…"}`;
  }
  const preset = (dfrom, dto) => { onChange(dfrom, dto); setOpen(false); };
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="tasks-chip-period"
        className="whitespace-nowrap rounded-full text-[11px] inline-flex items-center gap-1.5 transition-colors"
        style={{
          padding: "4px 10px",
          background: on ? "#F9731615" : "rgba(255,255,255,0.05)",
          color: on ? "#F97316" : "#EDEDED",
          border: `1px solid ${on ? "#F9731666" : "rgba(255,255,255,0.12)"}`,
          fontWeight: on ? 600 : 400,
        }}
      >
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center rounded-full flex-shrink-0"
          style={{ width: 20, height: 20, background: "#F9731626", color: "#F97316" }}
        >
          <CalendarDays size={14} strokeWidth={2.5} />
        </span>
        {label}
        {on ? (
          <span
            role="button"
            aria-label="Effacer la période"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange("", ""); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange("", ""); } }}
            className="inline-flex items-center justify-center rounded-full ml-0.5 hover:bg-white/10"
            style={{ width: 14, height: 14 }}
            data-testid="tasks-chip-period-clear"
            title="Effacer"
          >
            <X size={10} strokeWidth={2.4} />
          </span>
        ) : (
          <ChevronDown size={11} className="opacity-70" />
        )}
      </button>
      <PortalDropdown
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={260}
        maxHeight={360}
        testId="tasks-period-popover"
      >
        <div className="py-2">
          <div className="px-3 py-1 text-[9px] uppercase tracking-widest text-muted-foreground">Raccourcis</div>
          <button type="button" onClick={() => preset(todayIso(), todayIso())} className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.06]">Aujourd&apos;hui</button>
          <button type="button" onClick={() => preset(weekStartIso(), todayIso())} className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.06]">Cette semaine</button>
          <button type="button" onClick={() => preset(monthStartIso(), todayIso())} className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.06]">Ce mois</button>
          <div className="border-t border-white/10 my-1" />
          <div className="px-3 py-1 text-[9px] uppercase tracking-widest text-muted-foreground">Personnalisé</div>
          <div className="px-3 py-1.5 space-y-1.5">
            <div className="flex items-center gap-2 text-[11px]">
              <span className="w-8 text-muted-foreground">Du</span>
              <Input type="date" value={f} onChange={(e) => setF(e.target.value)} className="h-8 flex-1" />
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="w-8 text-muted-foreground">Au</span>
              <Input type="date" value={t} onChange={(e) => setT(e.target.value)} className="h-8 flex-1" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={() => { onChange(f, t); setOpen(false); }}
                disabled={!f && !t}
                className="flex-1 h-7 text-white border-0"
                style={{ background: "#F97316" }}
              >Appliquer</Button>
              {on && (
                <Button
                  size="sm" variant="outline"
                  onClick={() => { setF(""); setT(""); onChange("", ""); setOpen(false); }}
                  className="h-7 text-[11px]"
                >Effacer</Button>
              )}
            </div>
          </div>
        </div>
      </PortalDropdown>
    </>
  );
}

// ---------------- Generic single-select chip dropdown --------------------
function OptionChip({ label, icon: Ico, color, value, options, onChange, testId, compact = false, projects = null }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const on = !!value;
  const current = on ? (options.find((o) => o.v === value)?.l || label) : label;
  return (
    <div className={compact ? "flex-1" : undefined}>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        className={`whitespace-nowrap inline-flex items-center gap-1 transition-colors ${compact ? "w-full justify-center" : ""}`}
        style={{
          padding: compact ? "6px 10px" : "4px 10px",
          borderRadius: compact ? 15 : 9999,
          fontSize: compact ? 11 : 11,
          // Spec 2026-07-18 (batch 5 points) #5 — Option B (chips compacts) :
          // fond #26262A + bordure 1px #3A3A3F + texte #e8e8e5.
          background: on ? `${color}22` : (compact ? "#26262A" : "rgba(255,255,255,0.05)"),
          color: on ? color : (compact ? "#e8e8e5" : "#EDEDED"),
          border: `1px solid ${on ? `${color}66` : (compact ? "#3A3A3F" : "rgba(255,255,255,0.12)")}`,
          fontWeight: on ? 600 : 400,
        }}
      >
        {compact ? (
          <Ico size={12} strokeWidth={2.2} style={{ color, flexShrink: 0 }} />
        ) : (
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center rounded-full flex-shrink-0"
            style={{ width: 20, height: 20, background: `${color}26`, color }}
          >
            <Ico size={14} strokeWidth={2.5} />
          </span>
        )}
        {current}
        <ChevronDown size={11} className="opacity-70" />
      </button>
      <PortalDropdown
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={220}
        maxHeight={320}
        testId={`${testId}-popover`}
      >
        {projects ? (
          <div style={{ ...PANEL, boxShadow: "none", border: "none" }}>
            <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="w-full text-left hover:bg-[#26262C]" style={{ height: 34, padding: "0 10px", borderRadius: 6, fontSize: 12.5, color: !value ? color : "#E8E8EA", fontWeight: !value ? 600 : 400 }} data-testid={`${testId}-none`}>Tous</button>
            <div style={{ height: 1, background: "#2A2A30", margin: "3px 0" }} />
            <J3Groups projects={projects} isSelected={(p) => p.id === value} onPick={(p) => { onChange(p.id); setOpen(false); }} testIdPrefix={testId} />
          </div>
        ) : (
        <div className="py-1">
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); }}
            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.06] ${!value ? "text-vertec-blue font-medium" : ""}`}
          >
            Tous
          </button>
          <div className="border-t border-white/10 my-1" />
          {options.map((o, i, arr) => (
            <Fragment key={o.v}>{o.sep && i > 0 && <div className="border-t border-white/10 my-1" data-testid={`${testId}-siege-sep`} />}<button
              type="button"
              data-kind={o.kind}
              onClick={() => { onChange(o.v); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-white/[0.06] ${value === o.v ? "font-medium" : ""}`}
              style={value === o.v ? { color } : undefined}
            >
              {o.l}
            </button></Fragment>
          ))}
        </div>
        )}
      </PortalDropdown>
    </div>
  );
}


// -----------------------------------------------------------------------
// StatusMultiChip / PriorityChip — spec 2026-02-18 point 3
// Rendered as compact dropdowns (h ≈ 32 px) matching the search-bar height.
// -----------------------------------------------------------------------
function StatusMultiChip({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const arr = (value || "").split(",").map((s) => s.trim()).filter(Boolean);
  const toggle = (v) => {
    const set = new Set(arr);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange(Array.from(set).join(","));
  };
  const clear = (e) => { e.stopPropagation(); onChange(""); };
  const label = arr.length === 0 ? "Tous statuts" : `${arr.length} statut${arr.length > 1 ? "s" : ""}`;
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="filter-status"
        className="rounded-md flex items-center gap-1.5 whitespace-nowrap transition-colors"
        style={{
          height: 32, padding: "0 10px",
          background: arr.length ? "#0078D410" : "#232326",
          border: `1px solid ${arr.length ? "#0078D466" : "#2A2A2A"}`,
          color: arr.length ? "#5BA3E3" : "#E6E6E6",
          fontSize: 12,
          fontWeight: arr.length ? 600 : 400,
        }}
      >
        <Filter size={12} strokeWidth={2.2} />
        <span>{label}</span>
        {arr.length > 0 && <X size={12} onClick={clear} className="ml-0.5 hover:text-white" />}
        <ChevronDown size={12} />
      </button>
      <PortalDropdown
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={210}
        maxHeight={340}
      >
        {STATUS_OPTS.map((o) => {
          const checked = arr.includes(o.v);
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => toggle(o.v)}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white/[0.06] text-left"
              data-testid={`filter-status-opt-${o.v}`}
            >
              <span
                className="inline-flex items-center justify-center rounded-sm"
                style={{
                  width: 14, height: 14,
                  background: checked ? o.color : "transparent",
                  border: `1.5px solid ${o.color}`,
                }}
              >
                {checked && <CheckIcon size={10} strokeWidth={3} color="#FFFFFF" />}
              </span>
              <span className="inline-block rounded-full flex-shrink-0" style={{ width: 8, height: 8, background: o.color }} />
              <span style={{ color: "#EDEDED" }}>{o.l}</span>
            </button>
          );
        })}
      </PortalDropdown>
    </>
  );
}

function PriorityChip({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const sel = PRIO_OPTS.find((p) => p.v === value);
  const on = !!sel;
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="filter-priority"
        className="rounded-md flex items-center gap-1.5 whitespace-nowrap transition-colors"
        style={{
          height: 32, padding: "0 10px",
          background: on ? `${sel.color}10` : "#232326",
          border: `1px solid ${on ? `${sel.color}66` : "#2A2A2A"}`,
          color: on ? sel.color : "#E6E6E6",
          fontSize: 12,
          fontWeight: on ? 600 : 400,
        }}
      >
        {on && <span className="inline-block rounded-full" style={{ width: 8, height: 8, background: sel.color }} />}
        <span>{on ? sel.l : "Toutes priorités"}</span>
        {on && <X size={12} onClick={(e) => { e.stopPropagation(); onChange(""); }} className="ml-0.5 hover:opacity-70" />}
        <ChevronDown size={12} />
      </button>
      <PortalDropdown
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        minWidth={180}
        maxHeight={280}
      >
        <button
          type="button"
          onClick={() => { onChange(""); setOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white/[0.06] text-left text-muted-foreground"
        >
          Toutes priorités
        </button>
        <div style={{ borderTop: "1px solid #2A2A2A" }} />
        {PRIO_OPTS.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => { onChange(o.v); setOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white/[0.06] text-left ${value === o.v ? "font-semibold" : ""}`}
            data-testid={`filter-priority-opt-${o.v}`}
          >
            <span className="inline-block rounded-full" style={{ width: 10, height: 10, background: o.color }} />
            <span style={{ color: "#EDEDED" }}>{o.l}</span>
          </button>
        ))}
      </PortalDropdown>
    </>
  );
}

export default function FiltersBar({ filters, setFilters, projects, users, isSearchDebouncing = false }) {
  // Spec rev84 (2026-08-02) — état local pour afficher/masquer l'indice
  // clavier "/" à droite du champ de recherche unifié.
  const [searchFocused, setSearchFocused] = useState(false);
  const setF = (k, v) => setFilters((f) => ({
    ...f,
    [k]: v === NONE ? "" : v,
    page: 1,
  }));
  const setUnifiedSearch = (raw) => {
    const trimmed = raw.trimStart();
    const m = trimmed.match(REF_PATTERN);
    if (m) {
      const digits = m[1].toUpperCase();
      const ref = digits.startsWith("TH") ? digits : `TH${digits}`;
      setFilters((f) => ({ ...f, search: "", reference: ref, page: 1 }));
    } else {
      setFilters((f) => ({ ...f, search: trimmed, reference: "", page: 1 }));
    }
  };
  const clear = () => setFilters({
    search: "", reference: "", status: "", priority: "",
    project_id: "", created_by: "", assignee_id: "",
    date_from: "", date_to: "",
    scope: "", auto_only: "", page: 1,
  });
  const activeKeys = [
    "search", "reference", "status", "priority", "project_id",
    "created_by", "assignee_id", "date_from", "date_to", "scope", "auto_only",
  ];
  const activeCount = activeKeys.reduce((n, k) => n + (filters[k] ? 1 : 0), 0);
  const sortedUsers = useMemo(() => [...(users || [])].sort((a, b) =>
    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
  ), [users]);
  const userOptions = useMemo(
    () => sortedUsers.map((u) => ({ v: u.id, l: `${u.first_name} ${u.last_name}` })),
    [sortedUsers],
  );
  const projectOptions = useMemo(
    () => (projects || []).map((p, i, arr) => ({ v: p.id, l: p.name, kind: p.kind, sep: p.kind !== "siege" && arr[i - 1]?.kind === "siege" })), // iter417 pt 2 — Siège social en premier, trait de séparation
    [projects],
  );

  const unifiedValue = filters.reference ? `#${filters.reference}` : filters.search;

  return (
    <div
      className="rounded-[10px]"
      style={{ background: "#1E1E1E", border: "1px solid #2A2A2A", padding: 12 }}
      data-testid="filters-bar"
    >
      {/* -------------- Row 1 — search & basic filters ------------------- */}
      <div className="mb-1">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1.5">
          Recherche &amp; filtres à liste
        </div>
        <div
          className="flex items-center gap-2 flex-wrap"
          data-testid="filters-row-1"
        >
          <div className="relative flex-1 min-w-[240px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={unifiedValue}
              onChange={(e) => setUnifiedSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Objet, description, #TH0034…"
              data-testid="filter-search"
              data-no-capitalize
              className="w-full rounded-md focus:outline-none focus:ring-1 focus:ring-vertec-blue"
              style={{
                height: 32,
                paddingLeft: 30, paddingRight: 28,
                background: "#232326",
                border: "1px solid #2A2A2A",
                color: "#EDEDED",
                fontSize: 12,
              }}
            />
            <SearchClearButton value={unifiedValue} onClear={() => setUnifiedSearch("")} testid="filter-search-clear" />
            <SearchShortcutHint
              visible={!searchFocused && !unifiedValue}
              testid="filter-search-shortcut-hint"
            />
            {/* Spec rev163 (2026-02) — liseré animé pendant le debounce
                (250 ms). Jaune #F9BC26 (couleur du module Tâches). */}
            <SearchDebounceBar
              active={isSearchDebouncing}
              color="#F9BC26"
              testid="filter-search-debounce-bar"
            />
          </div>

          <StatusMultiChip value={filters.status} onChange={(v) => setF("status", v)} />
          <PriorityChip value={filters.priority} onChange={(v) => setF("priority", v)} />

          <PeriodChip
            from={filters.date_from}
            to={filters.date_to}
            onChange={(f, t) => setFilters((prev) => ({ ...prev, date_from: f || "", date_to: t || "", page: 1 }))}
          />

          <OptionChip
            label="Siège / Projet"
            icon={MapPin}
            color="#0078D4"
            value={filters.project_id}
            options={projectOptions}
            projects={projects || []}
            onChange={(v) => setF("project_id", v)}
            testId="tasks-chip-location"
          />

          {activeCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clear} data-testid="filter-clear" className="shrink-0 h-8">
              <X size={12} className="mr-1" /> Effacer ({activeCount})
            </Button>
          )}
        </div>
      </div>

      {/* Thin horizontal divider between the two logical rows */}
      <div style={{ borderTop: "1px solid #2A2A2A", margin: "12px -16px" }} />

      {/* -------------- Row 2 — Sélection directe (3 groupes + filtres avancés) --- */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
          Sélection directe
        </div>
        <div
          className="flex items-stretch flex-nowrap w-full quick-chips-scroller"
          style={{ justifyContent: "space-between", gap: 6 }}
          data-testid="quick-chips-row"
        >
          {QUICK_GROUPS.map((grp, gi) => (
            <Fragment key={grp.key}>
              <div
                className="flex items-center flex-1 justify-around"
                style={{ gap: 4 }}
                data-testid={`quick-group-${grp.key}`}
              >
                {grp.items.map((q) => {
                  const on = filters.scope === q.v;
                  const Ico = q.Icon;
                  return (
                    <button
                      type="button"
                      key={q.v}
                      onClick={() => setF("scope", on ? NONE : q.v)}
                      data-testid={`quick-${q.v}`}
                      className="whitespace-nowrap inline-flex items-center gap-1 transition-colors flex-1 justify-center"
                      style={{
                        // Spec 2026-07-18 (batch 5 points) #5 — Option B :
                        // fond #26262A + bordure 1px #3A3A3F + texte #e8e8e5.
                        padding: "6px 10px",
                        borderRadius: 15,
                        background: on ? `${q.color}22` : "#26262A",
                        color: on ? q.color : "#e8e8e5",
                        border: `1px solid ${on ? `${q.color}66` : "#3A3A3F"}`,
                        fontSize: 11,
                        fontWeight: on ? 600 : 400,
                      }}
                      title={on ? "Cliquez à nouveau pour désélectionner" : q.l}
                    >
                      <Ico size={12} strokeWidth={2.2} style={{ color: q.color, flexShrink: 0 }} />
                      <span>{q.l}</span>
                      {/* Spec iter171 pt.8 (2026-02-27) — croix visible pour signifier
                          explicitement qu'un re-clic désactive le filtre. */}
                      {on && (
                        <X size={10} strokeWidth={2.4} className="ml-0.5 opacity-80" />
                      )}
                    </button>
                  );
                })}
              </div>
              <span
                aria-hidden="true"
                className="shrink-0"
                style={{ width: 1, alignSelf: "stretch", background: "#2a2a28", margin: "4px 0" }}
              />
            </Fragment>
          ))}
          {/* iter143 — filtre « Tâches automatiques » (créées depuis un type de document) */}
          <button type="button" onClick={() => setF("auto_only", filters.auto_only ? "" : "true")} aria-pressed={!!filters.auto_only} className="whitespace-nowrap inline-flex items-center gap-1 transition-colors shrink-0" title="Afficher uniquement les tâches créées automatiquement depuis un type de document"
            style={{ padding: "6px 10px", borderRadius: 15, background: filters.auto_only ? "rgba(217,155,30,.14)" : "#26262A", color: filters.auto_only ? "#e6c383" : "#e8e8e5", border: `1px solid ${filters.auto_only ? "rgba(217,155,30,.5)" : "#3A3A3F"}`, fontSize: 11, fontWeight: filters.auto_only ? 600 : 400 }} data-testid="quick-auto-tasks">
            <span aria-hidden="true" className="inline-flex items-center justify-center" style={{ width: 11, height: 11, borderRadius: 3, border: `1px solid ${filters.auto_only ? "#e6c383" : "#71717c"}`, background: filters.auto_only ? "#D99B1E" : "transparent" }}>{filters.auto_only && <CheckIcon size={9} strokeWidth={3} style={{ color: "#1B1B1B" }} />}</span>
            <span>Tâches automatiques</span>
          </button>
          <span aria-hidden="true" className="shrink-0" style={{ width: 1, alignSelf: "stretch", background: "#2a2a28", margin: "4px 0" }} />
          {/* 4th "group" — advanced filters kept inline (spec 2026-02-18 fix) */}
          <div
            className="flex items-center flex-1 justify-around"
            style={{ gap: 4 }}
            data-testid="quick-group-advanced"
          >
            <OptionChip
              label="Créé par"
              icon={UserCog}
              color="#A855F7"
              value={filters.created_by}
              options={userOptions}
              onChange={(v) => setF("created_by", v)}
              testId="tasks-chip-created-by"
              compact
            />
            <OptionChip
              label="Assigné à"
              icon={Target}
              color="#EC4899"
              value={filters.assignee_id}
              options={userOptions}
              onChange={(v) => setF("assignee_id", v)}
              testId="tasks-chip-assignee"
              compact
            />
          </div>
        </div>
      </div>
      {/* Row 2 uses plain flex-wrap now — no more `overflow` on the parent
          so popovers rendered by <OptionChip> / <StatusMultiChip> etc. can
          extend below the container without being clipped. Spec 2026-02-18
          nonies : the previous `overflow-x-auto/overflow-y-hidden` combo
          triggered a visible scroll bar the moment a popover tried to
          push out of the row bounds. */}
      <style>{`
        .quick-chips-scroller {
          overflow: visible;
        }
      `}</style>
    </div>
  );
}
